// src/admin_roles/components/ModalRolePermissions.jsx
import React from "react";
import { Modal } from "react-bootstrap";

// Etiquetas legibles por módulo
const MODULE_LABELS = {
  admin_property: "Admin · Propiedades",
  admin_user: "Admin · Usuarios",
  admin_roles: "Admin · Roles",
  planification: "Planificación",
  rules: "Reglas",
  property_config: "Configuración de propiedad",
  inventory: "Inventario",
  blocks: "Bloqueos",
  ine: "INE",
  settings: "Ajustes",
};

// Etiquetas legibles por permiso
const PERM_LABELS = {
  // común
  can_access_module: "Acceso al módulo",

  // planification
  can_export_data: "Exportar datos",
  can_edit_cleaning: "Editar limpiezas",

  // rules
  can_edit_rules: "Editar reglas",

  // property_config
  can_edit_config: "Editar configuración",

  // inventory
  can_add_block: "Añadir bloqueos",
  can_edit_block: "Editar bloqueos",

  // blocks
  // (reutiliza can_edit_block)

  // ine
  can_edit_ine_config: "Editar configuración INE",
  can_sync_ine_data: "Sincronizar con INE",
  can_create_ine_report: "Crear informe",

  // admin_property
  can_add_property: "Añadir propiedades",
  can_edit_property: "Editar propiedades",
  can_delete_property: "Eliminar propiedades",

  // admin_user
  can_add_user: "Añadir usuarios",
  can_edit_user: "Editar usuarios",
  can_delete_user: "Eliminar usuarios",
  can_change_user_role: "Cambiar rol de usuario",

  // admin_roles
  can_add_role: "Añadir roles",
  can_edit_role: "Editar roles",
  can_delete_role: "Eliminar roles",
};

// Ordena mostrando primero can_access_module y luego alfabético
const ORDER_PERMS = (a, b) => {
  if (a[0] === "can_access_module") return -1;
  if (b[0] === "can_access_module") return 1;
  return a[0].localeCompare(b[0]);
};

// Componente de bloque por módulo
const ModulePermissions = ({ moduleKey, modulePerms }) => {
  if (!modulePerms) return null;
  const entries = Object.entries(modulePerms).sort(ORDER_PERMS);
  if (!entries.length) return null;

  return (
    <div className="mb-5">
      <h6 className="fw-bold mb-3">{MODULE_LABELS[moduleKey] || moduleKey}</h6>
      <div className="border rounded px-3 py-2">
        {entries.map(([permKey, value], idx) => (
          <div
            key={permKey}
            className={`d-flex align-items-center justify-content-between py-2 ${
              idx < entries.length - 1 ? "border-bottom" : ""
            }`}
          >
            <span className="text-gray-800">
              {PERM_LABELS[permKey] || permKey}
            </span>
            <span
              className={`badge ${
                value ? "badge-light-success" : "badge-light-secondary"
              }`}
            >
              {value ? "Sí" : "No"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ModalRolePermissions = ({ onClose, role }) => {
  if (!role) return null;

  // Orden deseado de módulos (admin primero)
  const modulesOrder = [
    "admin_property",
    "admin_user",
    "admin_roles",
    "planification",
    "rules",
    "property_config",
    "inventory",
    "blocks",
    "ine",
    "settings",
  ];

  const perms = role.permissions || {};
  const modulesWithAccess = modulesOrder.filter(
    (m) => perms?.[m]?.can_access_module
  ).length;

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Permisos del rol</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Cabecera rol */}
        <div className="mb-4">
          <div className="fw-bold fs-5">{role.name}</div>
          {role.description && (
            <div className="text-muted">{role.description}</div>
          )}

          <div className="mt-3">
            <span className="badge badge-light-primary">
              {modulesWithAccess} módulos con acceso
            </span>
          </div>
        </div>

        {/* Permisos por módulo */}
        {modulesOrder.map((moduleKey) => (
          <ModulePermissions
            key={moduleKey}
            moduleKey={moduleKey}
            modulePerms={perms?.[moduleKey]}
          />
        ))}

        {/* Si no hay ninguno */}
        {!modulesOrder.some((m) => perms?.[m]) && (
          <div className="alert alert-warning mb-0">
            Este rol no tiene permisos configurados.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-primary" onClick={onClose}>
          Cerrar
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalRolePermissions;
